import React, { useState } from 'react';
import { Plane, Lock, Mail, Eye, EyeOff, ArrowRight, Shield, Building2, UserCheck, User } from 'lucide-react';
import { Link } from 'react-router-dom';
interface LoginPageProps {
  onLogin: (role: string) => void;
}
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('tenant_admin');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      onLogin(selectedRole);
    }, 1000);
  };
  const roles = [{
    id: 'super_admin',
    label: 'Super Admin',
    desc: 'Platform Management',
    icon: Shield
  }, {
    id: 'tenant_admin',
    label: 'Company Admin',
    desc: 'Organization Management',
    icon: Building2
  }, {
    id: 'manager',
    label: 'Manager',
    desc: 'Team & Approvals',
    icon: UserCheck
  }, {
    id: 'traveler',
    label: 'Employee',
    desc: 'Travel & Bookings',
    icon: User
  }];
  return <div className="min-h-screen w-full flex items-center justify-center bg-slate-900 relative overflow-hidden" data-id="element-930">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" data-id="element-931"></div>
      <div className="absolute left-0 top-0 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl filter" data-id="element-932"></div>
      <div className="absolute right-0 bottom-0 h-96 w-96 rounded-full bg-indigo-500/10 blur-3xl filter" data-id="element-933"></div>

      <div className="relative w-full max-w-4xl p-6" data-id="element-934">
        <div className="mb-8 text-center" data-id="element-935">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-600 text-white shadow-xl shadow-blue-500/20" data-id="element-936">
            <Plane className="h-8 w-8" data-id="element-937" />
          </div>
          <h1 className="text-3xl font-bold text-white" data-id="element-938">TravelHub</h1>
          <p className="mt-2 text-slate-400" data-id="element-939">Enterprise Travel Management</p>
        </div>

        {/* Role Selector */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8" data-id="element-940">
          {roles.map(role => <div key={role.id} onClick={() => setSelectedRole(role.id)} className={`cursor-pointer rounded-xl p-4 border transition-all ${selectedRole === role.id ? 'bg-blue-600 border-blue-500 ring-2 ring-blue-400/50' : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800'}`} data-id="element-941">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center mb-3 ${selectedRole === role.id ? 'bg-white text-blue-600' : 'bg-slate-700 text-slate-400'}`} data-id="element-942">
                <role.icon className="h-5 w-5" data-id="element-943" />
              </div>
              <h3 className={`font-bold ${selectedRole === role.id ? 'text-white' : 'text-slate-200'}`} data-id="element-944">
                {role.label}
              </h3>
              <p className={`text-xs mt-1 ${selectedRole === role.id ? 'text-blue-100' : 'text-slate-500'}`} data-id="element-945">
                {role.desc}
              </p>
            </div>)}
        </div>

        <div className="mx-auto max-w-md rounded-2xl bg-white p-8 shadow-2xl" data-id="element-946">
          <div className="mb-6" data-id="element-947">
            <h2 className="text-xl font-semibold text-slate-900" data-id="element-948">Sign in</h2>
            <p className="text-sm text-slate-500" data-id="element-949">
              Access your {roles.find(r => r.id === selectedRole)?.label}{' '}
              account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-id="element-950">
            <div data-id="element-951">
              <label className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-952">
                Work Email
              </label>
              <div className="relative" data-id="element-953">
                <Mail className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" data-id="element-954" />
                <input type="email" required className="block w-full rounded-lg border border-slate-300 bg-slate-50 p-2.5 pl-10 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500" placeholder="name@company.com" data-id="element-955" />
              </div>
            </div>

            <div data-id="element-956">
              <label className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-957">
                Password
              </label>
              <div className="relative" data-id="element-958">
                <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" data-id="element-959" />
                <input type={showPassword ? 'text' : 'password'} required className="block w-full rounded-lg border border-slate-300 bg-slate-50 p-2.5 pl-10 pr-10 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:ring-blue-500" placeholder="••••••••" data-id="element-960" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-961">
                  {showPassword ? <EyeOff className="h-5 w-5" data-id="element-962" /> : <Eye className="h-5 w-5" data-id="element-963" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between" data-id="element-964">
              <div className="flex items-center" data-id="element-965">
                <input id="remember-me" type="checkbox" className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500" data-id="element-966" />
                <label htmlFor="remember-me" className="ml-2 text-sm text-slate-600" data-id="element-967">
                  Remember me
                </label>
              </div>
              <a href="#" className="text-sm font-medium text-blue-600 hover:text-blue-500 hover:underline" data-id="element-968">
                Forgot password?
              </a>
            </div>

            <button type="submit" disabled={isLoading} className="flex w-full items-center justify-center rounded-lg bg-blue-600 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 disabled:opacity-70" data-id="element-969">
              {isLoading ? <svg className="mr-2 h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-970">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-971"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-972"></path>
                </svg> : <>
                  Sign In <ArrowRight className="ml-2 h-4 w-4" data-id="element-973" />
                </>}
            </button>
          </form>

          <div className="mt-6 space-y-3" data-id="element-974">
            <div className="relative" data-id="element-975">
              <div className="absolute inset-0 flex items-center" data-id="element-976">
                <div className="w-full border-t border-slate-200" data-id="element-977"></div>
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-978">
                <span className="bg-white px-2 text-slate-500" data-id="element-979">or</span>
              </div>
            </div>

            <Link to="/register" className="flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-center text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-980">
              New company? Register your organization
            </Link>

            <Link to="/invite" className="flex w-full items-center justify-center rounded-lg border border-transparent px-5 py-2.5 text-center text-sm font-medium text-blue-600 hover:bg-blue-50 hover:underline" data-id="element-981">
              Have an invitation? Accept invite
            </Link>
          </div>
        </div>
      </div>
    </div>;
}